import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Plus, Package as PackageIcon, MagnifyingGlass, DownloadSimple, UploadSimple, CaretLeft, CaretRight, AirplaneTilt, Boat, Truck, Printer, Scales, Camera } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { RateTableViewer } from "@/components/RateTableViewer";
import BarcodeScannerDialog from "@/components/BarcodeScannerDialog";
import { HsCodeField } from "@/components/HsCodePicker";

const EMPTY = {
  sender_name: "", sender_address: "", recipient_name: "", recipient_address: "",
  recipient_phone: "", recipient_email: "", weight_kg: "", description: "", assigned_dispatcher_id: "",
  company_id: "", tracking_number: "", shipment_method: "air",
  recipient_lat: null, recipient_lng: null,
  signature_required: false,
  hs_code: "",
  unclaimed: false,
};

const KG_TO_LB = 2.20462;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Packages() {
  const { user } = useAuth();
  // Platform-level roles: super_admin AND every Kanal Super Warehouse level — all see + create across all companies.
  const isPlatform =
    user.role === "super_admin" ||
    user.role === "warehouse_super" ||
    user.role === "warehouse_super_l1" ||
    user.role === "warehouse_super_l2";
  // Level-1 Kanal Super Warehouse is restricted from viewing shipping labels
  // — hides the "failed to open label" toast that used to spam them on every
  // package creation.
  const canViewLabel = !(user.role === "warehouse_super" || user.role === "warehouse_super_l1");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [filter, setFilter] = useState(() => {
    // Honour ?status=... deep links from the Dashboard "Failed Attempts" widget
    if (typeof window !== "undefined") {
      const s = new URLSearchParams(window.location.search).get("status");
      if (s && ["pending", "in_transit", "delivered", "cancelled", "awaiting_redelivery", "return_to_sender"].includes(s)) {
        return s;
      }
    }
    return "all";
  });
  const [methodFilter, setMethodFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const withExceptions = searchParams.get("exceptions") === "1";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [dispatchers, setDispatchers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [weightUnit, setWeightUnit] = useState("kg"); // input/display toggle
  const importRef = React.useRef(null);
  // Recipient typeahead
  const [recipSuggestions, setRecipSuggestions] = useState([]);
  const [recipOpen, setRecipOpen] = useState(false);
  const recipDebounce = React.useRef(null);

  // User is read-only; super_admin CAN now create packages by picking a company
  const readOnly = user.role === "user" || user.role === "customer";
  const [courierRate, setCourierRate] = useState(null);
  const [rateViewerOpen, setRateViewerOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  // Route-zone match preview for ground packages (PRD #30)
  const [routeMatch, setRouteMatch] = useState(null);
  useEffect(() => {
    if (user.role !== "customer") return;
    api.get("/customer/courier-rate").then(({ data }) => setCourierRate(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Whenever the create form has ground method + recipient coords, preview the route match.
  useEffect(() => {
    if (!open) return;
    if (form.shipment_method !== "ground" || form.recipient_lat == null || form.recipient_lng == null) {
      setRouteMatch(null);
      return;
    }
    const params = { lat: form.recipient_lat, lng: form.recipient_lng };
    if (isPlatform && form.company_id) params.company_id = form.company_id;
    api.get("/routes/match", { params })
      .then(({ data }) => setRouteMatch(data.match || null))
      .catch(() => setRouteMatch(null));
  }, [open, form.recipient_lat, form.recipient_lng, form.shipment_method, form.company_id, user.role, isPlatform]);
  const canImportExport = !readOnly && (user.role === "super_admin" || (user.plan_features && user.plan_features.csv_import_export));

  const openDialogForSuperAdmin = isPlatform;

  const load = async () => {
    const params = { page, page_size: pageSize };
    if (filter !== "all") params.status = filter;
    if (methodFilter !== "all") params.shipment_method = methodFilter;
    if (search) params.search = search;
    if (withExceptions) params.with_exceptions = true;
    const { data } = await api.get("/packages", { params });
    setRows(data.items || []);
    setTotal(data.total || 0);
    if (isPlatform && companies.length === 0) {
      const c = await api.get("/companies");
      setCompanies(c.data);
    }
    if ((user.role === "company_admin" || user.role === "dispatcher" || isPlatform) && dispatchers.length === 0) {
      try {
        const u = await api.get("/users");
        setDispatchers(u.data.filter(x => x.role === "dispatcher"));
      } catch (e) { /* some roles can't */ }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, methodFilter, withExceptions, page]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const save = async () => {
    try {
      // Kanal Super Warehouse can flag a package as "unclaimed" — recipient
      // info is unknown until the customer contacts us. Skip recipient
      // validation in that case and stub out a placeholder email so the
      // pydantic schema still validates.
      const isUnclaimed = !!form.unclaimed;
      if (!isUnclaimed) {
        if (!form.recipient_name?.trim()) { toast.error("Recipient name is required"); return; }
        if (!form.recipient_email?.trim()) { toast.error("Recipient email is required"); return; }
        if (!EMAIL_RE.test(form.recipient_email.trim())) { toast.error("Enter a valid recipient email"); return; }
      }
      const weightVal = parseFloat(form.weight_kg || 0);
      const weight_kg = weightUnit === "lb" ? weightVal / KG_TO_LB : weightVal;
      const payload = { ...form, weight_kg };
      if (isUnclaimed) {
        payload.recipient_name = payload.recipient_name?.trim() || "Unclaimed — pending pickup";
        payload.recipient_email = payload.recipient_email?.trim() || "unclaimed@kanal.example.com";
        payload.recipient_address = payload.recipient_address?.trim() || "Held at Miami, FL warehouse";
      }
      if (!payload.assigned_dispatcher_id) delete payload.assigned_dispatcher_id;
      if (!payload.tracking_number?.trim()) delete payload.tracking_number;
      if (payload.recipient_lat == null) delete payload.recipient_lat;
      if (payload.recipient_lng == null) delete payload.recipient_lng;
      if (!isPlatform) delete payload.company_id;
      else if (!payload.company_id) { toast.error("Please select a company"); return; }
      const { data: created } = await api.post("/packages", payload);
      toast.success(isUnclaimed ? "Package logged as unclaimed" : "Package created");
      setOpen(false);
      setForm(EMPTY);
      load();
      // Auto-open the shipping label in a new tab (PRD #08).
      // Skip for Level-1 Kanal Super Warehouse — they don't have label access
      // so calling openLabel would trip the "failed to open" toast every time.
      if (created?.id && canViewLabel && !isUnclaimed) {
        openLabel(created.id);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openLabel = async (id) => {
    try {
      const resp = await api.get(`/packages/${id}/label`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const win = window.open(url, "_blank");
      if (!win) toast.message("Pop-ups blocked — label saved to your downloads");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Failed to open shipping label");
    }
  };

  const autoGenTracking = () => {
    let digits = "";
    for (let i = 0; i < 10; i++) digits += Math.floor(Math.random() * 10);
    setForm({ ...form, tracking_number: `KN${digits}` });
  };

  const filtered = rows;

  const companyName = (id) => companies.find(c => c.id === id)?.name || "—";

  const fetchRecipSuggestions = (q) => {
    if (recipDebounce.current) clearTimeout(recipDebounce.current);
    recipDebounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/packages/recipients/suggest", { params: { q } });
        setRecipSuggestions(data.suggestions || []);
        setRecipOpen((data.suggestions || []).length > 0);
      } catch (e) { /* ignore */ }
    }, 200);
  };

  const applySuggestion = (s) => {
    setForm({
      ...form,
      recipient_name: s.recipient_name,
      recipient_address: s.recipient_address || form.recipient_address,
      recipient_phone: s.recipient_phone || form.recipient_phone,
      recipient_email: s.recipient_email || form.recipient_email,
    });
    setRecipOpen(false);
  };

  const [exportOpen, setExportOpen] = useState(false);
  const [exportRange, setExportRange] = useState("all");

  const exportCSV = async (overrideRange) => {
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (methodFilter !== "all") params.shipment_method = methodFilter;
      const rng = overrideRange ?? exportRange;
      if (rng && rng !== "all") params.range = rng;
      const resp = await api.get("/packages/export.csv", { params, responseType: "blob" });
      // Try to read filename from Content-Disposition
      const cd = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(cd);
      const filename = match ? match[1] : "packages.csv";
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (e) { toast.error("Export failed"); }
  };

  const importCSV = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/packages/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} packages${data.errors?.length ? ` (${data.errors.length} errors)` : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="packages-page">
      <Toaster position="top-right" />
      <BarcodeScannerDialog open={scannerOpen} onOpenChange={setScannerOpen} />
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Fleet Operations</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>Packages</h1>
          {user.role === "customer" && (courierRate?.rate_table?.length > 0 || courierRate?.per_lb_rate != null) && (
            <button
              type="button"
              onClick={() => setRateViewerOpen(true)}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 dark:bg-cyan-500/10 dark:text-cyan-300 dark:border-cyan-500/30 dark:hover:bg-cyan-500/20 transition-colors"
              data-testid="packages-rate-table-btn"
            >
              <Scales size={12} weight="bold" />
              {courierRate?.rate_table?.length > 0
                ? `View ${courierRate.rate_table.length}-tier rate table`
                : `Shipping rate: ${courierRate.per_lb_rate_currency === "USD" ? "US$" : "J$"}${Number(courierRate.per_lb_rate).toLocaleString()}/lb`}
            </button>
          )}
          {user.role === "customer" && (
            <RateTableViewer
              open={rateViewerOpen}
              onOpenChange={setRateViewerOpen}
              rates={courierRate?.rate_table || []}
              currency={courierRate?.per_lb_rate_currency || "JMD"}
              companyName={courierRate?.company_name}
            />
          )}
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {canImportExport && (
            <>
              <Dialog open={exportOpen} onOpenChange={setExportOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="export-packages-btn">
                    <DownloadSimple size={15} className="mr-1.5" /> Export CSV
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md" data-testid="export-dialog">
                  <DialogHeader><DialogTitle>Export packages</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div className="text-sm text-zinc-600">
                      Current filters apply:
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200" data-testid="export-current-status">Status: {filter === "all" ? "All" : filter}</span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200" data-testid="export-current-method">Method: {methodFilter === "all" ? "All" : methodFilter}</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Date range</Label>
                      <Select value={exportRange} onValueChange={setExportRange}>
                        <SelectTrigger className="mt-1.5" data-testid="export-range"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all" data-testid="export-range-all">All time</SelectItem>
                          <SelectItem value="day" data-testid="export-range-day">Last 24 hours</SelectItem>
                          <SelectItem value="week" data-testid="export-range-week">Last 7 days</SelectItem>
                          <SelectItem value="month" data-testid="export-range-month">Last 30 days</SelectItem>
                          <SelectItem value="year" data-testid="export-range-year">Last 365 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setExportOpen(false)}>Cancel</Button>
                    <Button onClick={() => exportCSV()} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="confirm-export-btn">
                      <DownloadSimple size={14} weight="bold" className="mr-1.5" /> Download
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              {!readOnly && !isPlatform && (
                <>
                  <input type="file" accept=".csv" ref={importRef} className="hidden" onChange={(e) => e.target.files?.[0] && importCSV(e.target.files[0])} data-testid="import-packages-input" />
                  <Button variant="outline" onClick={() => importRef.current?.click()} data-testid="import-packages-btn">
                    <UploadSimple size={15} className="mr-1.5" /> Import
                  </Button>
                </>
              )}
            </>
          )}
        {!readOnly && (
          <>
            {(user.role === "warehouse" || user.role === "dispatcher" || user.role === "company_admin" || isPlatform) && (
              <Button
                variant="outline"
                onClick={() => setScannerOpen(true)}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                data-testid="scan-barcode-btn"
              >
                <Camera size={16} className="mr-1.5" weight="bold" /> Scan barcode
              </Button>
            )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="add-package-btn">
                <Plus size={16} className="mr-1.5" weight="bold" /> New Package
              </Button>
            </DialogTrigger>
            <DialogContent
              className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col"
              data-testid="package-dialog"
            >
              <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
                <DialogTitle>Create Package</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto px-5 sm:px-6 py-4 flex-1" data-testid="package-dialog-body">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-md bg-zinc-50 border border-zinc-200">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Tracking number</Label>
                    <div className="mt-1.5 flex gap-2">
                      <Input
                        value={form.tracking_number}
                        onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                        placeholder="Leave blank to auto-generate (KN…)"
                        data-testid="pkg-tracking-number"
                      />
                      <Button type="button" variant="outline" onClick={autoGenTracking} data-testid="pkg-tracking-auto" className="shrink-0">
                        Auto KN
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Shipment method</Label>
                    <Select value={form.shipment_method} onValueChange={(v) => setForm({ ...form, shipment_method: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="pkg-shipment-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="air">Air Freight</SelectItem>
                        <SelectItem value="sea">Ocean Freight</SelectItem>
                        <SelectItem value="ground">Ground</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Kanal Super Warehouse only — flag a package as unclaimed
                    when the parcel arrived without identifiable recipient info.
                    Skips recipient validation and lets the package sit in the
                    default-holding tenant's Unclaimed Packages queue until a
                    customer contacts us. */}
                {(user.role === "warehouse_super" || user.role === "warehouse_super_l1" || user.role === "warehouse_super_l2") && (
                  <label className="flex items-start gap-3 rounded-md border border-fuchsia-200 bg-fuchsia-50/50 px-3.5 py-3 cursor-pointer hover:bg-fuchsia-50 transition-colors" data-testid="pkg-unclaimed-label">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 rounded text-fuchsia-600 focus:ring-fuchsia-500"
                      checked={!!form.unclaimed}
                      onChange={(e) => setForm({ ...form, unclaimed: e.target.checked })}
                      data-testid="pkg-unclaimed"
                    />
                    <div className="text-sm">
                      <div className="font-semibold text-zinc-900">Flag as unclaimed — no identifiable recipient</div>
                      <div className="text-xs text-zinc-600 mt-0.5">
                    Skips recipient validation. The parcel will sit in <b>Unclaimed Packages</b> under the default-holding tenant until a customer contacts us to claim it.
                      </div>
                    </div>
                  </label>
                )}
                {/* Module 2.03 — signature-required toggle. Adds JMD 150 surcharge to invoice. */}
                {/* Hidden from warehouse role — they have no visibility into invoicing / surcharges. */}
                {user.role !== "warehouse" && !form.unclaimed && (
                  <label className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50/50 px-3.5 py-3 cursor-pointer hover:bg-amber-50 transition-colors" data-testid="pkg-signature-required-label">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 rounded text-amber-600 focus:ring-amber-500"
                      checked={!!form.signature_required}
                      onChange={(e) => setForm({ ...form, signature_required: e.target.checked })}
                      data-testid="pkg-signature-required"
                    />
                    <div className="text-sm">
                      <div className="font-semibold text-zinc-900">Require signature before delivery</div>
                      <div className="text-xs text-zinc-600 mt-0.5">
                        Driver must capture the recipient's signature at the door. Adds a <b>$150 JMD</b> surcharge to the invoice, itemised separately.
                      </div>
                    </div>
                  </label>
                )}
                <Field label="Sender name" value={form.sender_name} onChange={(v) => setForm({ ...form, sender_name: v })} testId="pkg-sender-name" />
                <div className="relative">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Recipient name</Label>
                  <Input
                    className="mt-1.5"
                    value={form.recipient_name}
                    onChange={(e) => {
                      setForm({ ...form, recipient_name: e.target.value });
                      fetchRecipSuggestions(e.target.value);
                    }}
                    onFocus={() => form.recipient_name && fetchRecipSuggestions(form.recipient_name)}
                    onBlur={() => setTimeout(() => setRecipOpen(false), 150)}
                    placeholder="Start typing..."
                    autoComplete="off"
                    data-testid="pkg-recipient-name"
                  />
                  {recipOpen && recipSuggestions.length > 0 && (
                    <div
                      className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md shadow-lg max-h-64 overflow-auto"
                      data-testid="recipient-suggestions"
                    >
                      {recipSuggestions.map((s, i) => (
                        <button
                          type="button"
                          key={`${s.recipient_name}-${i}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => applySuggestion(s)}
                          data-testid={`recipient-suggestion-${i}`}
                          className="w-full text-left px-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 border-b border-zinc-100 dark:border-zinc-800 last:border-0"
                        >
                          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{s.recipient_name}</div>
                          <div className="text-[11px] text-zinc-500 truncate">
                            {s.recipient_address}{s.recipient_phone ? ` · ${s.recipient_phone}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Sender address</Label>
                  <div className="mt-1.5">
                    <AddressAutocomplete
                      value={form.sender_address}
                      onChange={(v) => setForm({ ...form, sender_address: v })}
                      dataTestId="pkg-sender-address"
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Recipient address</Label>
                  <div className="mt-1.5">
                    <AddressAutocomplete
                      value={form.recipient_address}
                      onChange={(v) => setForm({ ...form, recipient_address: v, recipient_lat: null, recipient_lng: null })}
                      onLocationChange={(lat, lng) => setForm((f) => ({ ...f, recipient_lat: lat, recipient_lng: lng }))}
                      dataTestId="pkg-recipient-address"
                    />
                  </div>
                  {form.shipment_method === "ground" && form.recipient_lat != null && (
                    <div className="mt-2" data-testid="pkg-route-match-area">
                      {routeMatch ? (
                        <div
                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border"
                          style={{
                            backgroundColor: `${routeMatch.color}15`,
                            borderColor: `${routeMatch.color}55`,
                            color: routeMatch.color,
                          }}
                          data-testid="pkg-route-match-badge"
                        >
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: routeMatch.color }} />
                          Auto-tagged to route: <b className="font-semibold">{routeMatch.name}</b>
                          {(routeMatch.assigned_driver_ids?.length || 0) > 0 && (
                            <span className="font-normal opacity-80">· driver pre-selected</span>
                          )}
                        </div>
                      ) : (
                        <div className="text-[11px] text-zinc-400" data-testid="pkg-route-match-none">
                          No route zone covers this address — nearest driver will be auto-assigned
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Field label="Recipient phone" value={form.recipient_phone} onChange={(v) => setForm({ ...form, recipient_phone: v })} testId="pkg-phone" />
                <Field label="Recipient email *" value={form.recipient_email} onChange={(v) => setForm({ ...form, recipient_email: v })} testId="pkg-email" required />
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                    Weight
                    <span className="ml-2 inline-flex rounded-md border border-zinc-200 overflow-hidden text-[10px]">
                      <button type="button" onClick={() => setWeightUnit("kg")} className={`px-2 py-0.5 ${weightUnit === "kg" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600"}`} data-testid="weight-unit-kg">kg</button>
                      <button type="button" onClick={() => setWeightUnit("lb")} className={`px-2 py-0.5 ${weightUnit === "lb" ? "bg-zinc-900 text-white" : "bg-white text-zinc-600"}`} data-testid="weight-unit-lb">lb</button>
                    </span>
                  </Label>
                  <Input type="number" className="mt-1.5" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} data-testid="pkg-weight" />
                  {form.weight_kg && (
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {weightUnit === "kg"
                        ? `≈ ${(parseFloat(form.weight_kg) * KG_TO_LB).toFixed(2)} lb`
                        : `≈ ${(parseFloat(form.weight_kg) / KG_TO_LB).toFixed(2)} kg`}
                    </div>
                  )}
                </div>
                {openDialogForSuperAdmin && (
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Company (select)</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="pkg-company"><SelectValue placeholder="Select a company..." /></SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Description</Label>
                  <Textarea className="mt-1.5" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pkg-description" />
                </div>
                <div className="sm:col-span-2">
                  <HsCodeField
                    label="ASYCUDA HS Code (optional)"
                    hint="Overrides the manifest-wide HS code at customs export. Pick the code that best matches this package's contents."
                    value={form.hs_code}
                    onChange={(v) => setForm({ ...form, hs_code: v })}
                    testId="pkg-hs-code"
                  />
                </div>
                {dispatchers.length > 0 && (
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Assign dispatcher</Label>
                    <Select value={form.assigned_dispatcher_id} onValueChange={(v) => setForm({ ...form, assigned_dispatcher_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="pkg-dispatcher"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        {dispatchers.map(d => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              </div>
              <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0 gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="save-package-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </>
        )}
        </div>
      </div>

      {/* Exceptions filter notice */}
      {withExceptions && (
        <div
          className="flex items-center gap-3 mb-4 px-4 py-2.5 rounded-md border border-red-200 bg-red-50 text-red-800"
          data-testid="exceptions-filter-banner"
        >
          <div className="text-sm font-semibold flex-1">
            Showing only packages with open exception events
            <span className="ml-1 font-normal text-red-700">({total} match{total === 1 ? "" : "es"})</span>
          </div>
          <button
            type="button"
            onClick={() => { setSearchParams({}); setPage(1); }}
            className="text-xs font-semibold text-red-700 hover:text-red-900 underline-offset-2 hover:underline"
            data-testid="clear-exceptions-filter"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            placeholder="Search tracking, sender, recipient..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="!pl-11"
            data-testid="search-input"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_transit">In Transit</SelectItem>
            <SelectItem value="ready_for_pickup">Ready for Pickup</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="awaiting_redelivery">Awaiting Redelivery (1 strike)</SelectItem>
            <SelectItem value="return_to_sender">Return to Sender (2 strikes)</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={methodFilter} onValueChange={setMethodFilter}>
          <SelectTrigger className="w-44" data-testid="method-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="method-filter-all">All methods</SelectItem>
            <SelectItem value="air" data-testid="method-filter-air">Air Freight</SelectItem>
            <SelectItem value="sea" data-testid="method-filter-sea">Ocean Freight</SelectItem>
            <SelectItem value="ground" data-testid="method-filter-ground">Ground</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="packages-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Tracking</th>
              <th className="px-5 py-3 font-semibold">Sender</th>
              <th className="px-5 py-3 font-semibold">Recipient</th>
              <th className="px-5 py-3 font-semibold">Method</th>
              {isPlatform && <th className="px-5 py-3 font-semibold">Company</th>}
              <th className="px-5 py-3 font-semibold">Weight</th>
              <th className="px-5 py-3 font-semibold">Created</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={isPlatform ? 8 : 7} className="px-5 py-12 text-center text-zinc-500">
                <PackageIcon size={32} className="mx-auto mb-2 text-zinc-300" />
                No packages match.
              </td></tr>
            ) : filtered.map((p) => (
              <tr key={p.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`pkg-row-${p.id}`}>
                <td className="px-5 py-4">
                  <code className="text-xs font-mono text-zinc-900 bg-zinc-100 px-1.5 py-0.5 rounded">{p.tracking_number}</code>
                </td>
                <td className="px-5 py-4">
                  <div className="font-medium text-zinc-900">{p.sender_name}</div>
                  <div className="text-xs text-zinc-500 truncate max-w-[180px]">{p.sender_address}</div>
                </td>
                <td className="px-5 py-4">
                  <div className="font-medium text-zinc-900">{p.recipient_name}</div>
                  <div className="text-xs text-zinc-500 truncate max-w-[180px]">{p.recipient_address}</div>
                </td>
                <td className="px-5 py-4">
                  <ShipmentMethodBadge method={p.shipment_method} />
                </td>
                {isPlatform && <td className="px-5 py-4 text-zinc-700">{companyName(p.company_id)}</td>}
                <td className="px-5 py-4 text-zinc-700">
                  <div>{p.weight_kg} kg</div>
                  <div className="text-[11px] text-zinc-500">{(p.weight_kg * KG_TO_LB).toFixed(2)} lb</div>
                </td>
                <td className="px-5 py-4 text-zinc-700 text-xs whitespace-nowrap" data-testid={`pkg-created-${p.id}`}>
                  {p.created_at ? (
                    <>
                      <div>{new Date(p.created_at).toLocaleDateString()}</div>
                      <div className="text-[10px] text-zinc-500">{new Date(p.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </>
                  ) : "—"}
                </td>
                <td className="px-5 py-4"><StatusBadge status={p.status} /></td>
                <td className="px-5 py-4 text-right">
                  {canViewLabel && (
                    <button
                      type="button"
                      onClick={() => openLabel(p.id)}
                      title="Print shipping label"
                      data-testid={`label-pkg-${p.id}`}
                      className="inline-flex items-center text-xs font-semibold text-zinc-600 hover:text-zinc-900 mr-3"
                    >
                      <Printer size={13} weight="bold" className="mr-1" />
                      Label
                    </button>
                  )}
                  <Link to={`/packages/${p.id}`} className="text-xs font-semibold text-[#002BF6] hover:text-[#0021C7]" data-testid={`view-pkg-${p.id}`}>
                    Open 
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > pageSize && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-200 text-xs text-zinc-600" data-testid="pagination">
            <div>
              Showing <b>{(page - 1) * pageSize + 1}</b>–<b>{Math.min(page * pageSize, total)}</b> of <b>{total}</b>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="prev-page">
                <CaretLeft size={14} />
              </Button>
              <div className="px-3 py-1.5 border border-zinc-200 rounded-md bg-white">{page} / {totalPages}</div>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="next-page">
                <CaretRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>
      <Input type={type} className="mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

function ShipmentMethodBadge({ method }) {
  // Distinct color per method for at-a-glance scanning
  const m = (method || "air").toLowerCase();
  let cls = "bg-sky-50 text-sky-700 border border-sky-200";
  let Icon = AirplaneTilt;
  let label = "Air";
  if (m === "sea") {
    cls = "bg-cyan-50 text-cyan-700 border border-cyan-200";
    Icon = Boat;
    label = "Sea";
  } else if (m === "ground") {
    // Unique earthy emerald for Ground
    cls = "bg-emerald-50 text-emerald-700 border border-emerald-200";
    Icon = Truck;
    label = "Ground";
  }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}
      data-testid={`shipment-method-${m}`}
    >
      <Icon size={11} weight="bold" />
      {label}
    </span>
  );
}
